import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { requestPasswordReset, verifyPasswordResetOTP, resetPassword, normalizeRedirectPath } from '../../services/authService';
import loginBg from '../../assets/login_bg.jpg';
import { getSettings } from '../../models/settingsModel';
import { useAuth } from '../../context/AuthContext';
import '../../styles/login.css';
import { 
  FaUser, 
  FaLock, 
  FaMicrosoft, 
  FaEye, 
  FaEyeSlash, 
  FaArrowLeft, 
  FaSpinner,
  FaShieldAlt,
  FaExclamationTriangle
} from 'react-icons/fa';

function SimpleLogin() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Check if URL indicates forgot password flow
  const initialStep = window.location.pathname === '/login/forgot_password' ? 'forgot_password' : 'initial';

  // All state local to prevent external interference
  const [step, setStep] = useState(initialStep); // 'initial', 'password', 'otp', 'forgot_password', 'reset_otp', 'new_password'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState(null);
  // Form data
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [otpInfo, setOtpInfo] = useState({});
  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [passwordChangeToken, setPasswordChangeToken] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Check if user is already authenticated and redirect to dashboard
  React.useEffect(() => {
    if (!authLoading && isAuthenticated) {
      console.log('🔐 SimpleLogin: User already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);


  
  // Add global error handler
  React.useEffect(() => {
    const handleError = (event) => {
      console.log('🚨 SimpleLogin: Unhandled error caught:', event.error);
      event.preventDefault();
    };
    
    const handleRejection = (event) => {
      console.log('🚨 SimpleLogin: Unhandled promise rejection caught:', event.reason);
      event.preventDefault();
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    loadSettings();
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

     const loadSettings = async () => {
          try {
              const settingsResponse = await getSettings();
              
              if (settingsResponse && settingsResponse.status === 1 && settingsResponse.data) {
                  setSettings(settingsResponse.data);
              } else {
                  setSettings(null);
              }
          } catch (error) {
              console.error('🔧 loadSettings: Error loading settings via settingsModel:', error);
              console.error('🔧 loadSettings: Error details:', {
                  message: error.message,
                  name: error.name,
                  stack: error.stack?.split('\n').slice(0, 3), // First 3 lines of stack trace
                  isAuthError: error.message?.includes('authentication') || error.message?.includes('login') || error.response?.status === 401
              }); 
          }
      };
  


  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleMicrosoftLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const reactAppUrl = window.location.origin;
      window.location.href = `${API_BASE_URL}/login/azure?devOrigin=${encodeURIComponent(reactAppUrl)}`;
    } catch (err) {
      setError('Failed to initiate Microsoft login');
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!username.trim() || !password.trim()) {
      setError('Username and password are required');
      return;
    }
    
    setLoading(true);
    try {
      // Use direct fetch to avoid CORS issues, but integrate with AuthContext for state management
      const response = await fetch(`${API_BASE_URL}/api/auth_login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: username,
          password: password
        })
      });
      
      if (!response.ok && response.status !== 401) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (response.ok && data.status === 1) {
        // Complete login successful - update AuthContext state manually
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Trigger AuthContext to update by dispatching user change event
        window.dispatchEvent(new CustomEvent('userDataChanged', { 
          detail: { user: data.user } 
        }));
        
        // Check for stored redirect path
        const redirectPathRaw = data.redirectPath || localStorage.getItem('redirectAfterLogin');
        const redirectPath = normalizeRedirectPath(redirectPathRaw);
        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          console.log('🔄 Redirecting to stored path:', redirectPath);
          navigate(redirectPath, { replace: true });
        } else {
          console.log('🔄 Redirecting to dashboard');
          navigate('/dashboard', { replace: true });
        }
      } else if (data.status === 'otp_required') {
        // OTP required - set local state for OTP form
        setOtpToken(data.otp_token);
        setOtpInfo({
          userPhoneMasked: data.user_phone_masked,
          userEmailMasked: data.user_email_masked,
          otpMethod: data.otp_method,
          message: data.message
        });
        setStep('otp');
        setSuccess(data.message);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearMessages();
    
    if (!otp.trim() || otp.length !== 5) {
      setError('Please enter a valid 5-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      // Use direct fetch to avoid CORS issues
      const response = await fetch(`${API_BASE_URL}/api/verify_otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          otp: otp,
          otp_token: otpToken
        })
      });
      
      if (!response.ok && response.status !== 401) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (response.ok && data.status === 1) {
        console.log('🎯 OTP Success - Updating tokens and state');
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Trigger AuthContext to update by dispatching user change event
        window.dispatchEvent(new CustomEvent('userDataChanged', { 
          detail: { user: data.user } 
        }));
        
        // Check for stored redirect path
        const redirectPathRaw = data.redirectPath || localStorage.getItem('redirectAfterLogin');
        const redirectPath = normalizeRedirectPath(redirectPathRaw);
        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          console.log('🔄 Redirecting to stored path:', redirectPath);
          navigate(redirectPath, { replace: true });
        } else {
          console.log('🎯 OTP Success - Navigating to dashboard');
          navigate('/dashboard', { replace: true });
        }
      } else {
        console.log('🚨 OTP Failed - Response:', data);
        setError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    
    try {
      // Use direct fetch to avoid CORS issues
      const response = await fetch(`${API_BASE_URL}/api/resend_otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          otp_token: otpToken
        })
      });
      
      if (!response.ok && response.status !== 401) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (response.ok && data.status === 1) {
        setSuccess('A new verification code has been sent.');
      } else {
        setError(data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Password reset handlers
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!resetEmail.trim()) {
      setError('Username is required');
      return;
    }
    
    setLoading(true);
    try {
      // Add @silulumanzi.com domain to username (consistent with login)
      const fullEmail = resetEmail.trim() + '@silulumanzi.com';
      const response = await requestPasswordReset(fullEmail);
      if (response.status === 1) {
        setResetToken(response.data.reset_token);
        setSuccess(response.data.message);
        setStep('reset_otp');
      } else {
        setError(response.message || 'Failed to send reset code');
      }
    } catch (err) {
      setError('Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!resetOtp.trim() || resetOtp.length !== 5) {
      setError('Please enter the 5-digit verification code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await verifyPasswordResetOTP(resetToken, resetOtp);
      if (response.status === 1) {
        setPasswordChangeToken(response.data.password_change_token);
        setSuccess('Code verified! Please enter your new password.');
        setStep('new_password');
      } else {
        setError(response.message || 'Invalid or expired code');
      }
    } catch (err) {
      setError('Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('Both password fields are required');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      const response = await resetPassword(passwordChangeToken, newPassword);
      if (response.status === 1) {
        setSuccess('Password updated successfully! You can now log in.');
        // Reset all state and go back to login
        setStep('password');
        setResetEmail('');
        setResetToken('');
        setPasswordChangeToken('');
        setResetOtp('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(response.message || 'Failed to update password');
      }
    } catch (err) {
      setError('Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendPasswordResetOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!resetEmail.trim()) {
      setError('Email is missing. Please start the password reset process again.');
      return;
    }
    
    setLoading(true);
    try {
      // Add @silulumanzi.com domain to username (consistent with login)
      const fullEmail = resetEmail.trim() + '@silulumanzi.com';
      const response = await requestPasswordReset(fullEmail);
      if (response.status === 1) {
        setResetToken(response.data.reset_token);
        setSuccess(response.message || 'New password reset code sent');
        // Clear the OTP field so user enters new code
        setResetOtp('');
      } else {
        setError(response.message || 'Failed to resend reset code');
      }
    } catch (err) {
      setError('Failed to resend reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getOTPMethodText = () => {
    switch (otpInfo.otpMethod) {
      case 1: return `SMS to ${otpInfo.userPhoneMasked}`;
      case 2: return `email to ${otpInfo.userEmailMasked}`;
      case 3: return `SMS to ${otpInfo.userPhoneMasked} and email to ${otpInfo.userEmailMasked}`;
      default: return 'your registered contact method';
    }
  };

  const MessageAlert = ({ type, message }) => {
    if (!message) return null;
    const bgColor = type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200';
    const textColor = type === 'error' ? 'text-red-700' : 'text-green-700';
    const icon = type === 'error' ? <FaExclamationTriangle className="w-4 h-4" /> : null;
    return (
      <div className={`mb-4 p-4 ${bgColor} border rounded-lg flex items-start space-x-3`}>
        {icon && <div className={`${textColor} mt-0.5`}>{icon}</div>}
        <div className={`flex-1 ${textColor} text-sm leading-relaxed`}>{message}</div>
      </div>
    );
  };

  // OTP Form
  if (step === 'otp') {
    return (
      <div className="login-background min-h-screen flex items-center justify-center py-6 px-4 relative" style={{backgroundImage: `linear-gradient(rgba(0, 134, 212, 0.85), rgba(78, 173, 226, 0.85)), url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}>
        <div className="max-w-sm w-full">
          <div className="login-card bg-white rounded-lg p-6 border border-gray-100 animate-fadeIn shadow-lg">
            <div className="text-center mb-6">
              <div className="mx-auto h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-3">
                <FaShieldAlt className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h2>
              <p className="text-gray-600 mt-1 text-sm">Enter the verification code to complete login</p>
            </div>
            
            <MessageAlert type="error" message={error} />
            <MessageAlert type="success" message={success} /> 
            
            <form onSubmit={handleOTPVerification} className="space-y-4">
              <div className="text-center">
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-3">
                  Verification Code
                </label>
                <div className="flex justify-center">
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 5) {
                        setOtp(value);
                        setError('');
                      }
                    }}
                    placeholder="00000"
                    maxLength="5"
                    disabled={loading}
                    className="text-center text-xl font-mono border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tracking-widest transition-colors w-32 py-2.5"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Enter the 5-digit code you received
                </p>
              </div>
              
              <button 
                type="submit" 
                className="w-full flex items-center justify-center py-2.5 px-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-md transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:opacity-50 disabled:transform-none text-sm"
                disabled={loading || otp.length !== 5}
              >
                {loading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-3 w-3" />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  <span className="font-medium">Verify & Sign In</span>
                )}
              </button>
              
              <div className="space-y-2 pt-3">
                <div className="text-center">
                  <span className="text-xs text-gray-600">Didn't receive the code? </span>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="inline-flex items-center text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Resend code
                  </button>
                </div>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep('password')}
                    className="inline-flex items-center text-xs text-gray-600 hover:text-gray-700 font-medium transition-colors"
                  >
                    <FaArrowLeft className="mr-1 h-3 w-3" />
                    Back to login
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password Form
  if (step === 'forgot_password') {
    return (
      <div className="login-background min-h-screen flex items-center justify-center py-6 px-4 relative" style={{backgroundImage: `linear-gradient(rgba(0, 134, 212, 0.85), rgba(78, 173, 226, 0.85)), url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-blue-700/20"></div>
        <div className="max-w-sm w-full relative z-10">
          <div className="login-card bg-white rounded-lg p-6 border border-gray-100 animate-fadeIn shadow-lg">
            <div className="text-center mb-6">
              <div className="mx-auto h-12 w-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mb-3">
                <FaShieldAlt className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Reset Password</h2>
              <p className="text-gray-600 mt-1 text-sm">Enter your email to receive a reset code</p>
            </div>
            
            <MessageAlert type="error" message={error} />
            <MessageAlert type="success" message={success} />
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="reset_email" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-4 w-4 text-gray-400" />
                  </div>
                  <input 
                    type="text" 
                    id="reset_email" 
                    name="reset_email" 
                    value={resetEmail} 
                    onChange={(e) => setResetEmail(e.target.value)} 
                    placeholder="Enter your username" 
                    disabled={loading} 
                    autoComplete="username" 
                    className="login-input w-full pl-9 pr-3 py-2.5 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors text-sm" 
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Domain @silulumanzi.com will be added automatically</p>
              </div>
              
              <button 
                type="submit" 
                className="w-full flex items-center justify-center py-2.5 px-4 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-md transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 disabled:opacity-50 disabled:transform-none text-sm"
                disabled={loading || !resetEmail.trim()}
              >
                {loading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-3 w-3" />
                    <span>Sending...</span>
                  </div>
                ) : (
                  <span className="font-medium">Send Reset Code</span>
                )}
              </button>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setStep('password')}
                  className="inline-flex items-center text-xs text-gray-600 hover:text-gray-700 font-medium transition-colors"
                >
                  <FaArrowLeft className="mr-1 h-3 w-3" />
                  Back to login
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Reset OTP Verification Form
  if (step === 'reset_otp') {
    return (
      <div className="login-background min-h-screen flex items-center justify-center py-6 px-4 relative" style={{backgroundImage: `linear-gradient(rgba(0, 134, 212, 0.85), rgba(78, 173, 226, 0.85)), url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-blue-700/20"></div>
        <div className="max-w-sm w-full relative z-10">
          <div className="login-card bg-white rounded-lg p-6 border border-gray-100 animate-fadeIn shadow-lg">
            <div className="text-center mb-6">
              <div className="mx-auto h-12 w-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mb-3">
                <FaShieldAlt className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Verify Code</h2>
              <p className="text-gray-600 mt-1 text-sm">Enter the code sent to your email</p>
            </div>
            
            <MessageAlert type="error" message={error} />
            <MessageAlert type="success" message={success} />
            
            <form onSubmit={handleVerifyResetOTP} className="space-y-4">
              <div className="text-center">
                <label htmlFor="reset_otp" className="block text-sm font-medium text-gray-700 mb-3">Verification Code</label>
                <div className="flex justify-center">
                  <input 
                    type="text" 
                    id="reset_otp" 
                    name="reset_otp" 
                    value={resetOtp} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setResetOtp(value);
                    }}
                    placeholder="00000"
                    disabled={loading} 
                    autoComplete="one-time-code" 
                    className="login-input text-center text-xl font-mono tracking-widest w-32 py-2.5 border border-gray-300 focus:ring-orange-500 focus:border-orange-500 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors" 
                    maxLength="5"
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Enter the 5-digit code you received
                </p>
              </div>
              
              <button 
                type="submit" 
                className="w-full flex items-center justify-center py-2.5 px-4 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-md transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 disabled:opacity-50 disabled:transform-none text-sm"
                disabled={loading || resetOtp.length !== 5}
              >
                {loading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-3 w-3" />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  <span className="font-medium">Verify Code</span>
                )}
              </button>
              
              <div className="space-y-2 pt-3">
                <div className="text-center">
                  <span className="text-xs text-gray-600">Didn't receive the code? </span>
                  <button
                    type="button"
                    onClick={handleResendPasswordResetOTP}
                    disabled={loading}
                    className="inline-flex items-center text-xs text-orange-600 hover:text-orange-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Resend code
                  </button>
                </div>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep('forgot_password')}
                    className="inline-flex items-center text-xs text-gray-600 hover:text-gray-700 font-medium transition-colors"
                  >
                    <FaArrowLeft className="mr-1 h-3 w-3" />
                    Back to username
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // New Password Form
  if (step === 'new_password') {
    return (
      <div className="login-background min-h-screen flex items-center justify-center py-6 px-4 relative" style={{backgroundImage: `linear-gradient(rgba(0, 134, 212, 0.85), rgba(78, 173, 226, 0.85)), url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-blue-700/20"></div>
        <div className="max-w-sm w-full relative z-10">
          <div className="login-card bg-white rounded-lg p-6 border border-gray-100 animate-fadeIn shadow-lg">
            <div className="text-center mb-6">
              <div className="mx-auto h-12 w-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-3">
                <FaLock className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">New Password</h2>
              <p className="text-gray-600 mt-1 text-sm">Create a new secure password</p>
            </div>
            
            <MessageAlert type="error" message={error} />
            <MessageAlert type="success" message={success} />
            
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label htmlFor="new_password" className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type={showNewPassword ? "text" : "password"} 
                    id="new_password" 
                    name="new_password" 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    placeholder="Enter new password" 
                    disabled={loading} 
                    autoComplete="new-password" 
                    className="login-input w-full pl-10 pr-12 py-3 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors" 
                  />
                  <button 
                    type="button" 
                    className="absolute inset-y-0 right-0 pr-3 flex items-center" 
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? 
                      <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" /> : 
                      <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    }
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    id="confirm_password" 
                    name="confirm_password" 
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                    placeholder="Confirm new password" 
                    disabled={loading} 
                    autoComplete="new-password" 
                    className="login-input w-full pl-10 pr-12 py-3 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors" 
                  />
                  <button 
                    type="button" 
                    className="absolute inset-y-0 right-0 pr-3 flex items-center" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? 
                      <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" /> : 
                      <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    }
                  </button>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="w-full flex items-center justify-center py-3 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:opacity-50 disabled:transform-none"
                disabled={loading || !newPassword.trim() || !confirmPassword.trim() || newPassword !== confirmPassword}
              >
                {loading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                    <span>Updating...</span>
                  </div>
                ) : (
                  <span className="font-medium">Update Password</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="login-background min-h-screen flex items-center justify-center py-6 px-4 relative" style={{backgroundImage: `linear-gradient(rgba(0, 134, 212, 0.85), rgba(78, 173, 226, 0.85)), url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-blue-700/20"></div>
        <div className="max-w-sm w-full relative z-10">
          <div className="login-card bg-white rounded-lg p-6 border border-gray-100 animate-fadeIn shadow-lg">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600 text-sm">Checking authentication...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Login Form
  return (
    <div className="login-background min-h-screen flex items-center justify-center py-6 px-4 relative" style={{backgroundImage: `linear-gradient(rgba(0, 134, 212, 0.85), rgba(78, 173, 226, 0.85)), url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}>
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-blue-700/20"></div>
      <div className="max-w-sm w-full relative z-10">
        <div className="login-card bg-white rounded-lg p-6 border border-gray-100 animate-fadeIn shadow-lg">
          <div className="text-center mb-6">
              {settings?.site_logo_preview && settings.site_logo_preview !== 'Not available' ? (
            <div className="flex justify-center mb-3">
              <img 
                src={settings.site_logo_preview} 
                alt="Logo" 
                className="h-12 w-auto max-w-[200px] object-contain mx-auto" 
              />
            </div>
          ) :(
            <div className="mx-auto h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-3">
              <FaUser className="h-6 w-6 text-white" />
            </div>
          )}
            <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
            <p className="text-gray-600 mt-1 text-sm">Sign in to access your portal</p>
          </div>
          
          <MessageAlert type="error" message={error} />
          <MessageAlert type="success" message={success} />
          
          {step === 'initial' ? (
            <div className="space-y-3">
              <button 
                className="login-button microsoft-button group w-full flex items-center justify-center py-3 px-4 text-white rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:transform-none" 
                onClick={handleMicrosoftLogin} 
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                    <span className="font-medium">Redirecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <FaMicrosoft className="mr-2 h-4 w-4" />
                    <span className="font-medium">Sign in with Microsoft</span>
                  </div>
                )}
              </button>
              <div className="relative flex items-center justify-center my-4">
                <div className="border-t border-gray-300 absolute w-full"></div>
                <span className="bg-white px-3 text-xs text-gray-500 font-medium">OR</span>
              </div>
              <button 
                className="login-button password-button group w-full flex items-center justify-center py-3 px-4 text-white rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50" 
                onClick={() => setStep('password')}
              >
                <FaLock className="mr-2 h-4 w-4" />
                <span className="font-medium">Sign in with Password</span>
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-4 w-4 text-gray-400" />
                  </div>
                  <input 
                    type="text" 
                    id="username" 
                    name="username" 
                    value={username} 
                    onChange={(e) => setUsername(e.target.value)} 
                    placeholder="Enter your username" 
                    disabled={loading} 
                    autoComplete="username" 
                    className="login-input w-full pl-9 pr-3 py-2.5 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors text-sm" 
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Domain @silulumanzi.com will be added automatically</p>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-4 w-4 text-gray-400" />
                  </div>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    id="password" 
                    name="password" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    placeholder="Enter your password" 
                    disabled={loading} 
                    autoComplete="current-password" 
                    className="login-input w-full pl-9 pr-10 py-2.5 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors text-sm" 
                  />
                  <button 
                    type="button" 
                    className="absolute inset-y-0 right-0 pr-3 flex items-center" 
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 
                      <FaEyeSlash className="h-4 w-4 text-gray-400 hover:text-gray-600" /> : 
                      <FaEye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    }
                  </button>
                </div>
              </div>
              
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setStep('forgot_password')}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
              
              <div className="flex space-x-2 pt-2">
                <button 
                  type="submit" 
                  className="login-button password-button flex-1 flex items-center justify-center py-2.5 px-4 text-white rounded-md transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:opacity-50 disabled:transform-none text-sm" 
                  disabled={loading || !username.trim() || !password.trim()}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <FaSpinner className="animate-spin mr-2 h-3 w-3" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <span className="font-medium">Sign In</span>
                  )}
                </button>
                <button 
                  type="button" 
                  className="flex items-center justify-center py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm" 
                  onClick={() => setStep('initial')} 
                  disabled={loading}
                >
                  <FaArrowLeft className="mr-1 h-3 w-3" />
                  Back
                </button>
              </div>
            </form>
          )}
        </div>
        <div className="text-center mt-4">
          <p className="text-white text-xs opacity-75">© 2025 Silulumanzi Portal. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default SimpleLogin;